/**
 * The plain text of a paste, with Word's list markers made into words.
 *
 * Used when a paste has no HTML to clean -- a plain-text source, or
 * Cmd/Ctrl+Shift+V, which hands over text/plain alone; see `RichText`'s
 * `insertTransfer`. Plain text from Word or Outlook is not as plain as it
 * looks. A bulleted list
 * goes on the clipboard as a marker, a tab and the item: "·\tFirst point", the
 * marker being whichever glyph the bullet's font drew, so a middle dot for
 * Symbol, "o" for Courier New's second level and "§" for Wingdings' third.
 *
 * Left alone that did two wrong things. A paste that was nothing but a list --
 * every line with a tab, every line two cells wide -- is exactly what
 * `paste-table.ts` takes for a spreadsheet, so the bullets arrived as a bordered
 * two-column grid with a column of dots down the left: the pasted formatting
 * kept, and made worse. And a list among prose kept Word's glyphs and a raw tab,
 * which reads as "· First point" in the box and as whatever the recipient's
 * client makes of a tab in the email.
 *
 * So a line that is a marker, a tab and some text becomes "• text", or "1. text"
 * for a numbered one: a plain-text list, the way somebody would type one, and
 * the way Gmail's own "paste without formatting" leaves it. Not a real list --
 * whoever pasted without formatting asked for none, and the toolbar's list
 * button is one click away. (With the HTML present, `paste-html.ts` rebuilds
 * Word's list as a real one instead.)
 *
 * Only a line with exactly one tab, after the marker. A spreadsheet row that
 * happens to start with a number -- "1.\tWidget\t£4.20" -- still has its other
 * columns, and stays a table.
 */
export function plainPasteText(text: string): string {
  return text
    .split(/(\r\n?|\n)/)
    .map((line) => {
      const match = LIST_LINE.exec(line);
      if (!match) return line;
      const [, indent, marker, item] = match;
      return `${indent}${NUMBERED.test(marker!) ? marker : "•"} ${item}`;
    })
    .join("");
}

/*
 * Word's bullet glyphs as they come through as text, including the Symbol and
 * Wingdings private-use code points some versions emit, plus the dashes and
 * stars people use by hand; or a number or letter with a full stop or bracket.
 * Lone "o" is Word's second-level bullet and only counts with the tab after it.
 */
const LIST_LINE =
  /^([  ]*)([·••·§o▪■◦‣\-–*]|\(?[0-9]{1,3}[.)]|\(?[a-zA-Z][.)]|\(?[ivxIVX]{1,4}[.)])\t([^\t]*)$/;

const NUMBERED = /[.)]$/;
